import type { BrandId, ColorMode } from '@rammetec/design-system'
import { resolveFirebaseWebConfig } from './firebase.web'

type AppEnvironment = 'development' | 'staging' | 'production'

interface FirebaseEnv {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
  measurementId?: string
}

interface Env {
  appEnvironment: AppEnvironment
  designSystemBrand: BrandId
  designSystemMode: ColorMode
  /**
   * Opt-in flag for the monitoring + system-api stack (decision 0005).
   * When false (default), no WebSocket connections are opened.
   * When true, `VITE_MONITOR_WS_HOST` / `VITE_SYSTEM_API_HOST_WS` should be set.
   */
  monitorEnabled: boolean
  /** WebSocket base URL for the monitor service (e.g. ws://localhost:5300). */
  monitorWsHost?: string
  /** WebSocket base URL for the System API (e.g. ws://localhost:5101). */
  systemApiWsHost?: string
  /**
   * HTTP base URL for the Postgres REST API (system-api `/pg/v1`). When set,
   * `repositoryResolver.registerPostgresRestOptions()` is wired so descriptors
   * selecting the PostgresRest strategy can build repositories.
   */
  systemApiHost?: string
  /**
   * HTTP base URL for the VectorHub server (`POST {host}/jobs`). When set,
   * `repositoryResolver.registerRestScriptOptions()` can resolve legacy reads
   * via the VectorHub `data` module.
   */
  vectorhubApiHost?: string
  /**
   * When true in development, persist logs via the system-api transport
   * (spec 0015 D6 / request 0348). Default false — local console stays quiet.
   */
  logPersistInDev: boolean
  firebase: FirebaseEnv
}

function readString(name: string, fallback: string) {
  const value = import.meta.env[name]
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : fallback
}

function readOptional(name: string) {
  const value = import.meta.env[name]
  if (typeof value !== 'string' || value.trim().length === 0) return undefined
  return value.trim()
}

function readBoolean(name: string, fallback: boolean): boolean {
  const value = import.meta.env[name]
  if (typeof value !== 'string') return fallback
  return value.trim() === 'true'
}

/**
 * Config Firebase Web: env var vence, senao cai no mapa versionado
 * (`firebase.web.ts`) escolhido por `VITE_APP_ENV`.
 *
 * Em dev as `VITE_FIREBASE_*` vem do `.env` gerado pelo `rt env build` e nada
 * muda. No CI o build nao precisa mais dos 7 secrets. O `readRequired` do
 * resultado permanece: um valor vazio nos dois lados ainda estoura no bootstrap,
 * em vez de inicializar o Firebase com config invalida.
 */
function readFirebaseEnv(): FirebaseEnv {
  const fallback = resolveFirebaseWebConfig(readString('VITE_APP_ENV', 'development'))
  const resolve = (name: string, value: string) => {
    const fromEnv = readOptional(name)
    const resolved = fromEnv ?? value
    if (resolved.length === 0) {
      throw new Error(`Missing Firebase config: ${name} (env var e firebase.web.ts vazios)`)
    }
    return resolved
  }

  return {
    apiKey: resolve('VITE_FIREBASE_API_KEY', fallback.apiKey),
    authDomain: resolve('VITE_FIREBASE_AUTH_DOMAIN', fallback.authDomain),
    projectId: resolve('VITE_FIREBASE_PROJECT_ID', fallback.projectId),
    storageBucket: resolve('VITE_FIREBASE_STORAGE_BUCKET', fallback.storageBucket),
    messagingSenderId: resolve('VITE_FIREBASE_MESSAGING_SENDER_ID', fallback.messagingSenderId),
    appId: resolve('VITE_FIREBASE_APP_ID', fallback.appId),
    measurementId: readOptional('VITE_FIREBASE_MEASUREMENT_ID') ?? fallback.measurementId,
  }
}

function readAppEnvironment(): AppEnvironment {
  const value = readString('VITE_APP_ENV', 'development')
  if (value === 'development' || value === 'staging' || value === 'production') {
    return value
  }
  throw new Error(`Invalid VITE_APP_ENV: ${value}`)
}

function readColorMode(): ColorMode {
  const value = readString('VITE_DS_MODE', 'auto')
  if (value === 'light' || value === 'dark' || value === 'auto') return value
  throw new Error(`Invalid VITE_DS_MODE: ${value}`)
}

export const env: Env = {
  appEnvironment: readAppEnvironment(),
  designSystemBrand: readString('VITE_DS_BRAND', 'syscolect') as BrandId,
  designSystemMode: readColorMode(),
  monitorEnabled: readBoolean('VITE_MONITOR_ENABLED', false),
  monitorWsHost: readOptional('VITE_MONITOR_WS_HOST'),
  systemApiWsHost: readOptional('VITE_SYSTEM_API_HOST_WS'),
  systemApiHost: readOptional('VITE_SYSTEM_API_HOST'),
  vectorhubApiHost: readOptional('VITE_VECTORHUB_API_HOST'),
  logPersistInDev: readBoolean('VITE_LOG_PERSIST_IN_DEV', false),
  firebase: readFirebaseEnv(),
}
